import { useState, useEffect, useRef } from 'react';
import { 
  Search, Package, FileText, X, Clock, Loader2,
  LayoutDashboard, CheckSquare, ClipboardCheck, PlusCircle,
  TrendingDown, RefreshCw, Sparkles, Shield, MapPin,
  FolderTree, Wrench, Trash2, Upload, Users, Sliders,
  Settings, HelpCircle, BarChart3
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { searchService, type SearchResult } from '@/services/search-service';
import { useImportAssetsModal } from '@/contexts/ImportAssetsModalContext';

// Helper component to render search results content - extracted to fix nested ternary (S3358)
function SearchResultsContent({
  error,
  isLoading,
  results,
  searchQuery,
  children,
}: {
  error: string | null;
  isLoading: boolean;
  results: unknown[];
  searchQuery: string;
  children: React.ReactNode;
}) {
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-destructive mb-2">Search failed</p>
        <p className="text-xs text-muted-foreground">{error}</p>
        <p className="text-xs text-muted-foreground mt-2">
          Try switching to Demo mode in settings if the backend is unavailable.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Searching...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No results found for "{searchQuery}"
      </div>
    );
  }

  return <>{children}</>;
}

// Icon mapping for feature results
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Package,
  CheckSquare,
  ClipboardCheck,
  PlusCircle,
  TrendingDown,
  RefreshCw,
  Sparkles,
  Search,
  FileText,
  Shield,
  MapPin,
  FolderTree,
  Wrench,
  Trash2,
  Upload,
  Users,
  Sliders,
  Settings,
  HelpCircle,
  BarChart3,
};

export function GlobalSearch(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'demo' | 'live'>('demo');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { openModal } = useImportAssetsModal();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent searches and data source from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent searches', e);
      }
    }

    // Check data source mode
    const mode = localStorage.getItem('app-mode');
    setDataSource(mode === 'live' ? 'live' : 'demo');

    // Listen for storage changes (when mode is changed in settings)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'app-mode') {
        setDataSource(e.newValue === 'live' ? 'live' : 'demo');
      }
    };

    // Listen for custom event for same-window changes
    const handleModeChange = () => {
      const mode = localStorage.getItem('app-mode');
      setDataSource(mode === 'live' ? 'live' : 'demo');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('app-mode-changed', handleModeChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('app-mode-changed', handleModeChange);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearch = (result: SearchResult) => {
    const updated = [
      result,
      ...recentSearches.filter((r) => r.id !== result.id),
    ].slice(0, 5); // Keep only last 5
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // Perform search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSelectedIndex(0);
      setError(null);
      return;
    }

    const performSearch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // searchService automatically handles demo vs live mode via dataService
        const searchResults = await searchService.search(searchQuery);
        setResults(searchResults);
        setSelectedIndex(0);
      } catch (err) {
        console.error('Search error:', err);
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dataSource]);

  // Handle keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const displayResults = searchQuery ? results : recentSearches;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % displayResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + displayResults.length) % displayResults.length);
    } else if (e.key === 'Enter' && displayResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(displayResults[selectedIndex]);
    }
  };

  const handleImageError = (resultId: string) => {
    setImageErrors((prev) => new Set(prev).add(resultId));
  };

  // Helper function to render result icon - extracted to avoid nested ternary
  const renderResultIcon = (result: SearchResult): React.ReactNode => {
    if (result.type !== 'asset') {
      return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
    
    const showImage = result.imageUrl && !imageErrors.has(result.id);
    if (showImage) {
      return (
        <img
          src={result.imageUrl}
          alt={result.title}
          className="h-full w-full object-cover"
          onError={() => handleImageError(result.id)}
        />
      );
    }
    
    return <Package className="h-5 w-5 text-muted-foreground" />;
  };

  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(result);
    setOpen(false);
    setSearchQuery('');
    
    // Special handling for Import Assets - open modal instead of navigating
    if (result.route === '/administration/imports' || result.route === '/assets/import' || result.id === 'import-assets') {
      openModal();
      return;
    }
    
    navigate(result.route);
  };

  const assetResults = results.filter((r) => r.type === 'asset');
  const transactionResults = results.filter((r) => r.type === 'transaction');
  const featureResults = results.filter((r) => r.type === 'feature');

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={`Search assets, transactions, features...${dataSource === 'live' ? '' : ' (Demo)'}`}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="h-9 w-full pl-9 pr-20 text-sm"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {searchQuery && !isLoading && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                setSearchQuery('');
                inputRef.current?.focus();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:inline-flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Dropdown results */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[500px] overflow-y-auto rounded-md border bg-popover shadow-lg">
          {/* Recent searches */}
          {!searchQuery && recentSearches.length > 0 && (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-xs hover:bg-accent"
                  onClick={clearRecentSearches}
                >
                  Clear
                </Button>
              </div>
              <div className="space-y-1">
                {recentSearches.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(result);
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                      selectedIndex === index && "bg-accent"
                    )}
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted overflow-hidden">
                      {renderResultIcon(result)}
                    </div>
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-sm">{result.title}</span>
                        {result.badge && (
                          <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                            {result.badge}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{result.subtitle}</span>
                        {result.metadata && (
                          <>
                            <span className="flex-shrink-0">•</span>
                            <span className="truncate">{result.metadata}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Clock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search results */}
          {searchQuery && (
            <SearchResultsContent
              error={error}
              isLoading={isLoading}
              results={results}
              searchQuery={searchQuery}
            >
              <div className="p-2">
                  {/* Asset results */}
                  {assetResults.length > 0 && (
                    <div className="mb-2">
                      <div className="px-2 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Assets</span>
                      </div>
                      <div className="space-y-1">
                        {assetResults.map((result, index) => (
                          <button
                            key={result.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSelect(result);
                            }}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                              selectedIndex === index && "bg-accent"
                            )}
                          >
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted overflow-hidden">
                              {renderResultIcon(result)}
                            </div>
                            <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                              <div className="flex items-center gap-2">
                                <span className="truncate font-medium text-sm">{result.title}</span>
                                {result.badge && (
                                  <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                                    {result.badge}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="truncate">{result.subtitle}</span>
                                {result.metadata && (
                                  <>
                                    <span className="flex-shrink-0">•</span>
                                    <span className="truncate">{result.metadata}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transaction results */}
                  {transactionResults.length > 0 && (
                    <div className={featureResults.length > 0 ? 'mb-2' : ''}>
                      <div className="px-2 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Transactions</span>
                      </div>
                      <div className="space-y-1">
                        {transactionResults.map((result, index) => {
                          const actualIndex = assetResults.length + index;
                          return (
                            <button
                              key={result.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelect(result);
                              }}
                              className={cn(
                                "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                                selectedIndex === actualIndex && "bg-accent"
                              )}
                            >
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                                <div className="flex items-center gap-2">
                                  <span className="truncate font-medium text-sm">{result.title}</span>
                                  {result.badge && (
                                    <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                                      {result.badge}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className="truncate">{result.subtitle}</span>
                                  {result.metadata && (
                                    <>
                                      <span className="flex-shrink-0">•</span>
                                      <span className="truncate">{result.metadata}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Feature/Screen results */}
                  {featureResults.length > 0 && (
                    <div>
                      <div className="px-2 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Features</span>
                      </div>
                      <div className="space-y-1">
                        {featureResults.map((result, index) => {
                          const actualIndex = assetResults.length + transactionResults.length + index;
                          const IconComponent = result.iconName && iconMap[result.iconName] ? iconMap[result.iconName] : Search;
                          return (
                            <button
                              key={result.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelect(result);
                              }}
                              className={cn(
                                "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                                selectedIndex === actualIndex && "bg-accent"
                              )}
                            >
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted">
                                <IconComponent className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                                <span className="truncate font-medium text-sm">{result.title}</span>
                                <span className="truncate text-xs text-muted-foreground">{result.subtitle}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
            </SearchResultsContent>
          )}

          {/* Empty state */}
          {!searchQuery && recentSearches.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Start typing to search assets, transactions, and features...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
